import { useCallback } from 'react';
import { runInAction } from 'mobx';
import { store } from 'Models/Store';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { logErrorOnServer } from 'Services/Api/_HumanWritten/ClientsideErrorLoggingService';

export const useServerVersionCheck = () => {
	const getCurrentBrowserVersion = useCallback((): string => {
		const ua = navigator.userAgent;
		let tem;
		let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
		if (/trident/i.test(M[1])) {
			tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
			return `IE ${tem[1] || ''}`;
		}
		if (M[1] === 'Chrome') {
			tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
			if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
		}
		M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
		tem = ua.match(/version\/(\d+)/i);
		if (tem != null) M.splice(1, 1, tem[1]);
		return M.join(' ');
	}, []);

	const sendBrowserVersionToServer = useCallback(() => {
		const browserVersion = getCurrentBrowserVersion();

		// SEND THE REQUEST HERE
		logErrorOnServer({
			loggedInUserId: store.userId,
			currentLocation: document.location.href.toString(),
			previousLocation: document.referrer,
			message: 'Version pop-up has appeared 5+ times for the logged in user.',
			stack: `Browser version: ${browserVersion.toString()}`,
		}).then(() => {
			// Do nothing (the error will be logged in the server)
		});
	}, [getCurrentBrowserVersion]);

	const checkServerVersion = useCallback(
		(version: string | undefined | null) => {
			if (!version) return;

			const versionTime = 'new_version_time';
			const versionCount = 'new_version_count';

			const time = Number(localStorage.getItem(versionTime) ?? 0);
			const count = Number(localStorage.getItem(versionCount) ?? 0);

			if (version !== window.buildVersion.buildRef) {
				if (new Date().getTime() - time < 60 * 60 * 1000) {
					localStorage.setItem(versionCount, String(count + 1));

					if (count === 5) {
						sendBrowserVersionToServer();
					}

					return;
				}

				// Reset the values in localStorage
				localStorage.setItem(versionTime, new Date().getTime().toString());
				localStorage.setItem(versionCount, '0');

				runInAction(() => {
					if (isNullOrUndefined(store.versionPopUpTimestamp)) {
						store.hasServerVersionMismatch = true;
					}
				});
			}
		},
		[sendBrowserVersionToServer],
	);

	return {
		checkServerVersion,
	};
};
